/**
 * Tamaño del teclado de asignar (Plan, móvil): el tirador de arriba lo
 * estira. Todo se expresa como una `escala` de las teclas: 1 = tamaño normal;
 * al crecer, solo las cuatro filas de teclas se hacen más altas (las píldoras
 * y los márgenes no cambian), así que el panel crece hacia arriba.
 */

/** Alto de una tecla a escala 1 (px). */
export const ALTO_TECLA_BASE = 48;
/** Tamaño de la cifra de una tecla a escala 1 (px). */
export const FUENTE_TECLA_BASE = 21;
/** Filas de teclas (7 8 9 − · 4 5 6 + · 1 2 3 = · ✕ 0 ⌫ Hecho). */
export const FILAS_TECLAS = 4;
/** Crecimiento máximo del panel respecto a su alto normal (+45 %). */
export const CRECIMIENTO_MAXIMO = 0.45;
/** Fracción máxima de la pantalla que puede ocupar el panel. */
export const FRACCION_PANTALLA_MAXIMA = 0.6;
/** Tope absoluto de la escala mientras aún no se ha medido el panel. */
export const ESCALA_TOPE = 2;

/** Alto de una tecla (px enteros) a la escala dada. */
export function altoTecla(escala: number): number {
  return Math.round(ALTO_TECLA_BASE * normalizarEscala(escala));
}

/**
 * La cifra crece la mitad que la tecla (de 21 px a ~29 px con +77 %), para
 * que se note el cambio sin que los números se vean desproporcionados.
 * Redondeado a medio píxel.
 */
export function fuenteTecla(escala: number): number {
  const e = normalizarEscala(escala);
  return Math.round(FUENTE_TECLA_BASE * (1 + (e - 1) / 2) * 2) / 2;
}

/**
 * Alto que tendría el panel a escala 1, a partir de lo que mide ahora con la
 * escala `escala` (solo las filas de teclas cambian de alto).
 */
export function altoPanelBase(altoMedido: number, escala: number): number {
  const extra = FILAS_TECLAS * (altoTecla(escala) - ALTO_TECLA_BASE);
  return Math.max(0, altoMedido - extra);
}

/**
 * Escala máxima: el panel puede crecer hasta +45 % de su alto normal o hasta
 * el 60 % de la pantalla, lo que sea menor. Nunca por debajo de 1 (si el
 * panel ya ocupa más del 60 %, simplemente no se estira).
 */
export function escalaMaxima({
  altoBase,
  altoVentana,
}: {
  altoBase: number;
  altoVentana: number;
}): number {
  if (!(altoBase > 0) || !(altoVentana > 0)) {
    return 1;
  }
  const altoMaximo = Math.min(
    altoBase * (1 + CRECIMIENTO_MAXIMO),
    altoVentana * FRACCION_PANTALLA_MAXIMA,
  );
  const extra = Math.max(0, altoMaximo - altoBase);
  return Math.min(ESCALA_TOPE, 1 + extra / (FILAS_TECLAS * ALTO_TECLA_BASE));
}

/** Escala válida (número finito ≥ 1); cualquier otra cosa vale 1. */
export function normalizarEscala(escala: unknown): number {
  return typeof escala === 'number' && Number.isFinite(escala) && escala > 1
    ? escala
    : 1;
}

/** Limita la escala a [1, máximo]. */
export function limitarEscala(escala: unknown, maximo: number): number {
  return Math.min(normalizarEscala(escala), Math.max(1, maximo));
}

/**
 * Escala tras arrastrar el tirador `desplazamiento` px hacia ARRIBA (negativo
 * = hacia abajo) desde una escala inicial: el borde superior del panel sigue
 * al dedo, porque todo el crecimiento se reparte entre las cuatro filas.
 */
export function escalaTrasArrastre({
  escalaInicial,
  desplazamiento,
  maximo,
}: {
  escalaInicial: number;
  desplazamiento: number;
  maximo: number;
}): number {
  return limitarEscala(
    normalizarEscala(escalaInicial) +
      desplazamiento / (FILAS_TECLAS * ALTO_TECLA_BASE),
    maximo,
  );
}

/** Doble toque en el tirador: de normal a máximo y de cualquier otro a normal. */
export function alternarEscala(escala: number, maximo: number): number {
  return normalizarEscala(escala) > 1.01 ? 1 : Math.max(1, maximo);
}

/**
 * Píxeles que el panel baja ENTERO tras arrastrar el tirador hacia abajo:
 * primero se gasta en volver al tamaño normal y lo que sobra lo baja (para
 * cerrarlo deslizando, como las demás hojas). 0 si no ha bajado de más.
 */
export function bajadoTrasArrastre({
  escalaInicial,
  desplazamiento,
}: {
  escalaInicial: number;
  desplazamiento: number;
}): number {
  const encoger =
    (normalizarEscala(escalaInicial) - 1) * FILAS_TECLAS * ALTO_TECLA_BASE;
  return Math.max(0, -desplazamiento - encoger);
}
